import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Shield,
  Download,
  Search,
  Filter,
  Users,
  TrendingUp,
  FileText,
  Loader2,
  ChevronRight,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPage() {
  const navigate = useNavigate();
  const { isAdmin, isAuthenticated } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    score_min: '',
    score_max: '',
    loan_band: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    avgScore: 0,
    ready: 0,
    notReady: 0
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      navigate('/');
      toast.error('Admin access required');
      return;
    }
    fetchAssessments();
  }, [isAuthenticated, isAdmin, navigate]);

  const fetchAssessments = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.score_min) params.score_min = parseInt(filters.score_min);
      if (filters.score_max) params.score_max = parseInt(filters.score_max);
      if (filters.loan_band && filters.loan_band !== 'all') params.loan_band = filters.loan_band;
      
      const response = await adminApi.getAssessments(params);
      setAssessments(response.data.assessments);
      
      // Calculate stats
      const total = response.data.assessments.length;
      const avgScore = total > 0 
        ? Math.round(response.data.assessments.reduce((acc, a) => acc + a.total_score, 0) / total)
        : 0;
      const ready = response.data.assessments.filter(a => a.total_score >= 70).length;
      const notReady = response.data.assessments.filter(a => a.total_score < 50).length;
      
      setStats({ total, avgScore, ready, notReady });
    } catch (error) {
      console.error('Failed to fetch assessments:', error);
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await adminApi.exportCsv();
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Failed to export CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  const handleApplyFilters = () => {
    fetchAssessments();
  };

  const getScoreBadgeClass = (category) => {
    switch (category) {
      case 'Strong Lending Candidate':
        return 'badge success';
      case 'Funding Ready':
        return 'badge info';
      case 'Preparation Required':
        return 'badge warning';
      default:
        return 'badge danger';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />
      
      <main className="flex-1 py-8 md:py-12" data-testid="admin-page">
        <div className="container-main">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 font-heading">
                  Admin Dashboard
                </h1>
                <p className="text-slate-600">Manage assessments and leads</p>
              </div>
            </div>
            <Button onClick={handleExportCSV} className="btn-primary" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Total Assessments', value: stats.total, icon: FileText, color: 'bg-slate-100 text-slate-600' },
              { label: 'Average Score', value: stats.avgScore, icon: TrendingUp, color: 'bg-sky-100 text-sky-600' },
              { label: 'Funding Ready', value: stats.ready, icon: Users, color: 'bg-emerald-100 text-emerald-600' },
              { label: 'Not Ready', value: stats.notReady, icon: Users, color: 'bg-red-100 text-red-600' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card-default p-5"
              >
                <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center mb-3`}>
                  <stat.icon className="w-5 h-5" />
                </div>
                <div className="text-2xl font-bold text-slate-900 tabular-nums">
                  {stat.value}
                </div>
                <div className="text-sm text-slate-500">{stat.label}</div>
              </motion.div>
            ))}
          </div>

          {/* Filters */}
          <div className="card-default p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-5 h-5 text-slate-500" />
              <span className="font-medium text-slate-900">Filters</span>
            </div>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Min Score</Label>
                <Input
                  type="number"
                  placeholder="0"
                  min="0"
                  max="100"
                  value={filters.score_min}
                  onChange={(e) => setFilters({ ...filters, score_min: e.target.value })}
                  data-testid="filter-min-score"
                />
              </div>
              <div className="space-y-2">
                <Label>Max Score</Label>
                <Input
                  type="number"
                  placeholder="100"
                  min="0"
                  max="100"
                  value={filters.score_max}
                  onChange={(e) => setFilters({ ...filters, score_max: e.target.value })}
                  data-testid="filter-max-score"
                />
              </div>
              <div className="space-y-2">
                <Label>Funding Amount</Label>
                <Select 
                  value={filters.loan_band} 
                  onValueChange={(value) => setFilters({ ...filters, loan_band: value })}
                >
                  <SelectTrigger data-testid="filter-loan-band">
                    <SelectValue placeholder="All amounts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All amounts</SelectItem>
                    <SelectItem value="under_250k">Under $250,000</SelectItem>
                    <SelectItem value="250k_to_1m">$250k - $1M</SelectItem>
                    <SelectItem value="above_1m">Above $1M</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button onClick={handleApplyFilters} className="w-full" data-testid="apply-filters-btn">
                  <Search className="w-4 h-4 mr-2" />
                  Apply Filters
                </Button>
              </div>
            </div>
          </div>

          {/* Assessments Table */}
          <div className="card-default overflow-hidden">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Business Name</th>
                    <th>Type</th>
                    <th>Funding Amount</th>
                    <th>Score</th>
                    <th>Category</th>
                    <th>Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin text-sky-500 mx-auto" />
                      </td>
                    </tr>
                  ) : assessments.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-slate-500">
                        No assessments found
                      </td>
                    </tr>
                  ) : (
                    assessments.map((assessment) => (
                      <tr key={assessment.id} data-testid={`assessment-row-${assessment.id}`}>
                        <td className="font-medium text-slate-900">
                          {assessment.business_name}
                        </td>
                        <td className="capitalize text-slate-600">
                          {assessment.business_status}
                        </td>
                        <td className="text-slate-600">
                          {assessment.loan_amount_band.replace(/_/g, ' ')}
                        </td>
                        <td>
                          <span className="font-semibold text-slate-900 tabular-nums">
                            {assessment.total_score}
                          </span>
                        </td>
                        <td>
                          <span className={getScoreBadgeClass(assessment.score_category)}>
                            {assessment.score_category}
                          </span>
                        </td>
                        <td className="text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(assessment.created_at).toLocaleDateString()}
                          </span>
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/results/${assessment.id}`)}
                          >
                            View
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-slate-500 text-center">
            Showing {assessments.length} assessment{assessments.length !== 1 ? 's' : ''}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
